/**
 * useIsAppFullScreen Hook
 *
 * Detects whether the AppLayout is in fullscreen mode (header/footer hidden).
 * Listens for custom events from AppLayout and storage events for cross-tab sync.
 *
 * @returns boolean - true if app is in fullscreen mode
 */

import { useState, useEffect } from 'react';
import { Brand } from '@enfomentis/shared/config';
import { APP_FULLSCREEN_EVENT } from '@/components/shared/layouts/AppLayout';

/** localStorage key for fullscreen state */
const STORAGE_KEY = `${Brand.cachePrefix}-fullscreen`;

/**
 * Safely read fullscreen state from localStorage
 * Returns false if value is missing, corrupted, or parse fails
 */
function getStoredFullscreenState(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) return false;
    return JSON.parse(stored) === true;
  } catch {
    // Corrupted value - return safe default
    return false;
  }
}

/**
 * Hook to detect AppLayout fullscreen mode
 *
 * - Reads initial value from localStorage
 * - Listens for APP_FULLSCREEN_EVENT (same-tab changes)
 * - Listens for storage events (cross-tab sync)
 */
export function useIsAppFullScreen(): boolean {
  const [isFullScreen, setIsFullScreen] = useState<boolean>(getStoredFullscreenState);

  useEffect(() => {
    // Handler for same-tab fullscreen changes (custom event from AppLayout)
    const handleFullscreenEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ isFullScreen: boolean }>;
      setIsFullScreen(customEvent.detail.isFullScreen);
    };

    // Handler for cross-tab changes (storage event)
    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setIsFullScreen(getStoredFullscreenState());
      }
    };

    // Subscribe to events
    window.addEventListener(APP_FULLSCREEN_EVENT, handleFullscreenEvent);
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener(APP_FULLSCREEN_EVENT, handleFullscreenEvent);
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, []);

  return isFullScreen;
}
